import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { useTheme } from "next-themes";
import { AccountManager } from "@/components/Accounts/AccountManager";
import { CurrencySettings } from "@/components/Settings/CurrencySettings";
import { CategoryManager } from "@/components/Settings/CategoryManager";
import { Moon, Sun, Monitor, Trash2, AlertTriangle, Loader2, Settings as SettingsIcon } from "lucide-react";
import {
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const Settings = () => {
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    
    setIsDeleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Delete all user data from tables (in order due to foreign keys)
      // Balance snapshots first (references accounts and wallets)
      await supabase.from("balance_snapshots").delete().eq("user_id", user.id);
      
      // Wallets next (references accounts)
      await supabase.from("wallets").delete().eq("user_id", user.id);
      
      // Then other user data
      await supabase.from("transactions").delete().eq("user_id", user.id);
      await supabase.from("accounts").delete().eq("user_id", user.id);
      await supabase.from("budgets").delete().eq("user_id", user.id);
      await supabase.from("budget_milestones").delete().eq("user_id", user.id);
      await supabase.from("financial_goals").delete().eq("user_id", user.id);
      await supabase.from("recurring_transactions").delete().eq("user_id", user.id);
      await supabase.from("mood_entries").delete().eq("user_id", user.id);
      await supabase.from("spending_habits").delete().eq("user_id", user.id);
      await supabase.from("detected_subscriptions").delete().eq("user_id", user.id);
      await supabase.from("categories").delete().eq("user_id", user.id);
      await supabase.from("wellness_insights").delete().eq("user_id", user.id);
      await supabase.from("user_preferences").delete().eq("user_id", user.id);

      // Sign out the user
      await supabase.auth.signOut();
      
      toast({ 
        title: "Account deleted", 
        description: "Your account and all data have been permanently deleted." 
      });
      
      navigate("/auth");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({ 
        title: "Error", 
        description: "Failed to delete account. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setIsDeleting(false);
      setDialogOpen(false);
      setDeleteConfirmText("");
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
      <PageHeader
        icon={SettingsIcon}
        title="Settings"
        description="Manage your account settings and preferences"
      />

      <div className="grid gap-4 md:grid-cols-2">
          <Card className="bg-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Appearance</CardTitle>
              <CardDescription className="text-xs">Choose your preferred theme</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  variant={theme === "light" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTheme("light")}
                  className="flex-1"
                >
                  <Sun className="h-4 w-4 mr-2" />
                  Light
                </Button>
                <Button
                  variant={theme === "dark" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTheme("dark")}
                  className="flex-1"
                >
                  <Moon className="h-4 w-4 mr-2" />
                  Dark
                </Button>
                <Button
                  variant={theme === "system" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTheme("system")}
                  className="flex-1"
                >
                  <Monitor className="h-4 w-4 mr-2" />
                  System
                </Button>
              </div>
            </CardContent>
          </Card>

          <CurrencySettings />
        </div>

        <Card className="bg-card/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Account Management</CardTitle>
            <CardDescription className="text-xs">
              Create and manage your accounts. Set parent accounts to create hierarchies.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AccountManager />
          </CardContent>
        </Card>

        <Card className="bg-card/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Category Management</CardTitle>
            <CardDescription className="text-xs">
              View and manage your transaction categories
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CategoryManager />
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Danger Zone
            </CardTitle>
            <CardDescription className="text-xs">
              Irreversible actions that will permanently affect your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-lg border border-destructive/30 bg-background">
              <div>
                <h4 className="font-medium text-sm">Delete Account</h4>
                <p className="text-xs text-muted-foreground">
                  Permanently delete your account and all associated data
                </p>
              </div>
              <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-5 w-5" />
                      Delete Your Account?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-3">
                      <p>
                        This action <strong>cannot be undone</strong>. This will permanently delete:
                      </p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        <li>All your accounts and wallets</li>
                        <li>All transactions and recurring transactions</li>
                        <li>All budgets and financial goals</li>
                        <li>All mood entries and wellness data</li>
                        <li>All your preferences and settings</li>
                      </ul>
                      <div className="pt-2">
                        <Label htmlFor="confirmDelete" className="text-foreground">
                          Type <strong>DELETE</strong> to confirm:
                        </Label>
                        <Input
                          id="confirmDelete"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="DELETE"
                          className="mt-2"
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== "DELETE" || isDeleting}
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Forever
                        </>
                      )}
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
    </div>
  );
};

export default Settings;